import { useMemo } from 'react'
import { useWatch } from 'react-hook-form'

import Box from '@material-ui/core/Box'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import { useDebouncedCallback } from '@react-hookz/web'
import classNames from 'classnames'

import { type ProductFormSchema, useProductFormContext } from '~/products/hooks/useProductForm'
import PricingTable from './active-discount-table'

const FIELD_WIDTH = 125

export const usePricingStyles = makeStyles((theme) => ({
    pricesErrorMessage: {
        color: theme.palette.error.main,
        marginLeft: 15,
        fontSize: '0.9rem',
    },
    pricingSection: {
        marginTop: 25,
        width: 'auto',
    },
    pricingSectionTitle: {
        marginBottom: 20,
    },
    pricingRow: {
        display: 'flex',
        alignItems: 'center',
    },
    setAllRow: {
        marginBottom: 15,
        paddingBottom: 15,
        borderBottom: '1px solid lightgray',
    },
    setAllMessage: {
        marginRight: 15,
        width: 265,
        color: 'gray',
    },
    priceField: {
        height: 35,
    },
    pricingDisabledMessage: {
        marginTop: 15,
        marginBottom: 15,
        fontSize: '0.9rem',
        color: 'grey',
    },
}))

export default function ActiveDiscountPricing() {
    const classes = usePricingStyles()
    const { control, getValues, setValue } = useProductFormContext()

    const stockUnits = useWatch({
        control,
        name: 'stock_units',
        exact: true,
        defaultValue: [],
    })
    const channelProducts = useWatch({
        control,
        name: 'channel_products',
        exact: true,
        defaultValue: [],
    })
    const productDiscounts = useWatch({
        control,
        name: 'product_discounts',
        exact: true,
        defaultValue: [],
    })

    const activeChannelStockUnitDiscounts = useWatch({
        control,
        exact: true,
        name: 'active_channel_stock_unit_discounts',
        defaultValue: [],
    })

    const channelProductWithDiscounts = useMemo(() => {
        const channelProductIdsWithDiscount = activeChannelStockUnitDiscounts.reduce(
            (cpSet, csud) => {
                if (cpSet.has(csud.channel_product_id)) {
                    return cpSet
                }
                cpSet.add(csud.channel_product_id)
                return cpSet
            },
            new Set<number>()
        )

        const shopIds = new Set<number>()
        return channelProducts.filter((cp) => {
            if (!cp.shop) return false
            if (shopIds.has(cp.shop.id) || !channelProductIdsWithDiscount.has(Number(cp.id))) {
                return false
            }
            shopIds.add(cp.shop.id)
            return true
        })
    }, [channelProducts, activeChannelStockUnitDiscounts])

    const discountIndexById = useMemo(
        () => Object.fromEntries(productDiscounts.map((d, index) => [d.discount.id, index])),
        [productDiscounts]
    )

    const activeDiscountIndexLookup = useMemo(() => {
        return activeChannelStockUnitDiscounts.reduce((map, csud, index) => {
            const key = `${csud.shop_id}_${csud.stock_unit_id}`
            const value = map.get(key) ?? []

            return map.set(key, [...value, index])
        }, new Map<string, number[]>())
    }, [activeChannelStockUnitDiscounts])

    const discountIndexByCSUId = useMemo(
        () =>
            Object.fromEntries(
                activeChannelStockUnitDiscounts.map((csud) => [
                    csud.channel_stock_unit_id,
                    discountIndexById[csud.discount_id],
                ])
            ),
        [activeChannelStockUnitDiscounts, discountIndexById]
    )

    const handleSetPriceForAll = useDebouncedCallback(
        (price: number) => {
            if (isNaN(price)) {
                return
            }

            const activeChannelStockUnitDiscounts = getValues(
                'active_channel_stock_unit_discounts'
            ).map((csud) => ({
                ...csud,
                price,
            }))

            setValue('active_channel_stock_unit_discounts', activeChannelStockUnitDiscounts)
        },
        [getValues, setValue],
        300
    )

    const handleSetPriceForChannelProduct = useDebouncedCallback(
        (cp: (typeof channelProducts)[number], value: number) => {
            if (isNaN(value)) {
                return
            }

            cp.channel_stock_units.forEach(
                (
                    csu: ProductFormSchema['channel_products'][number]['channel_stock_units'][number]
                ) => {
                    if (csu.placeholder) {
                        return
                    }

                    const indexes =
                        activeDiscountIndexLookup.get(`${cp.shop.id}_${csu.stock_unit?.id}`) ?? []
                    indexes.forEach((index) => {
                        setValue(`active_channel_stock_unit_discounts.${index}.price`, value)
                    })
                }
            )
        },
        [setValue, activeDiscountIndexLookup],
        300
    )

    const handleSetPriceForStockUnits = useDebouncedCallback(
        (stockUnitId: number, value: number) => {
            if (isNaN(value)) {
                return
            }

            const indexesToUpdate = getValues('channel_products').flatMap((cp) => {
                return cp.channel_stock_units
                    .filter((csu) => !csu.placeholder && csu.stock_unit?.id === stockUnitId)
                    .flatMap((csu) => {
                        return activeDiscountIndexLookup.get(`${cp.shop.id}_${csu.stock_unit?.id}`)
                    })
                    .filter((index) => index !== undefined)
            })

            indexesToUpdate.forEach((index) => {
                setValue(`active_channel_stock_unit_discounts.${index}.price`, value)
            })
        },
        [getValues, setValue, activeDiscountIndexLookup],
        300
    )

    if (!activeChannelStockUnitDiscounts.length) {
        return <div style={{ padding: '20px 0' }}>{gettext('No active discount')}</div>
    }

    return (
        <div>
            <div className={classes.pricingSection}>
                {(stockUnits.length > 1 || channelProductWithDiscounts.length > 1) && (
                    <div className={classNames(classes.pricingRow, classes.setAllRow)}>
                        <div>
                            <Typography className={classes.setAllMessage} variant="body2">
                                {gettext('Use this field to set all prices at once')}
                            </Typography>
                        </div>

                        <Box width={FIELD_WIDTH}>
                            <TextField
                                type="number"
                                variant="outlined"
                                margin="dense"
                                placeholder={gettext('Set all')}
                                InputProps={{ className: classes.priceField }}
                                inputProps={{ min: 0 }}
                                onChange={(e) => handleSetPriceForAll(parseFloat(e.target.value))}
                            />
                        </Box>
                    </div>
                )}

                <PricingTable
                    channelProducts={channelProductWithDiscounts}
                    stockUnits={stockUnits}
                    fieldWidth={FIELD_WIDTH}
                    activeDiscountIndexLookup={activeDiscountIndexLookup}
                    discountIndexByCSUId={discountIndexByCSUId}
                    onSetPriceForChannelProduct={handleSetPriceForChannelProduct}
                    onSetPriceForStockUnits={handleSetPriceForStockUnits}
                />
            </div>
        </div>
    )
}
